import { useCallback } from 'react';
import { odata } from '@bingads-webui/http-util';
import { useOdataOptions } from './use-odata-options';

/**
 * BulkUpsert hook for creating/updating a PerformanceMax campaign and its
 * campaign-level brand kit in a single round-trip.
 *
 * We don't use `@bingads-webui/edm-bulk` here because it is schema-driven
 * and the shared MT EDM schema does not expose the Campaign Brand Kit columns
 * (Brand Color, Brand Logo, Term Exclusions, Messaging Restrictions).
 */

// --- Public types --------------------------------------------------------

export type BrandLogoSubType = 'SquareLogoMedia' | 'LandscapeLogoMedia';

export interface BrandLogoAssetRef {
  /** Asset Library asset id (returned by AddAsset). */
  assetId: string;
  subType: BrandLogoSubType;
}

export interface BulkUpsertCampaignBrandKitRequest {
  /** Existing campaign id. Omit to create a new campaign. */
  campaignId?: string | number;
  campaignName: string;
  status?: 'Active' | 'Paused';
  /** MT enum string, e.g. 'PacificTimeUSCanadaTijuana', 'BogotaLimaQuito'. */
  timeZone: string;
  /** Daily budget value, e.g. 50 or 274.37. */
  budget: number;
  budgetType?: 'DailyBudgetStandard' | 'DailyBudgetAccelerated';
  /** Language token, e.g. 'All', 'English'. Defaults to 'All'. */
  language?: string;

  // --- CampaignBrandKit ---
  businessName?: string;
  /** Hex strings, e.g. ['#000000', '#FF0000']. */
  brandColors?: string[];
  /** Logo references with required subType. Asset must exist in Asset Library. */
  brandLogos?: BrandLogoAssetRef[];
  termExclusions?: string[];
  messagingRestrictions?: string[];
}

export interface BulkUpsertCampaignBrandKitResponse {
  /** Newly created or updated campaign id, or undefined when MT returns an Error row. */
  campaignId?: string;
  /** Server-side error code from the response Error column, when present. */
  error?: string;
  /** Raw header → values map, useful for debugging. */
  raw?: Record<string, string>;
}

// --- CSV row builders ----------------------------------------------------

/**
 * Builds the JSON-stringified text-asset array for Brand Color, Term Exclusions,
 * and Messaging Restrictions columns.
 *
 * Output (before CSV+JSON escaping): `[{"Text":"#FFFFFF"}, ...]`
 *
 * @param {string[]} values - Array of text values to wrap.
 * @returns {string} JSON array literal.
 */
const textAssetArrayJson = (values: string[]): string => {
  const items = values.map(v => `{"Text":${JSON.stringify(v)}}`);
  return `[${items.join(',')}]`;
};

/**
 * Builds the JSON-stringified asset-link array for the Brand Logo column.
 *
 * Output (before CSV+JSON escaping): `[{"id":123,"subType":"SquareLogoMedia"}, ...]`
 *
 * @param {BrandLogoAssetRef[]} logos - Asset references with subType.
 * @returns {string} JSON array literal.
 */
const brandLogoArrayJson = (logos: BrandLogoAssetRef[]): string => {
  const items = logos.map(l => `{"id":${JSON.stringify(l.assetId)},"subType":${JSON.stringify(l.subType)}}`);
  return `[${items.join(',')}]`;
};

/**
 * Wraps a JSON-array string in CSV quotes so embedded commas don't split the row.
 * The double-quotes are doubled (CSV's escape rule) so they survive parsing.
 *
 * @param {string} jsonArrayString - The raw JSON array literal.
 * @returns {string} CSV-quoted string.
 */
const csvQuoteJson = (jsonArrayString: string): string => `"${jsonArrayString.replace(/"/g, '""')}"`;

/**
 * Escapes a plain CSV field. Wraps in double quotes if it contains comma,
 * quote, or newline; doubles any embedded quotes.
 *
 * @param {string} value - Field value.
 * @returns {string} CSV-safe field.
 */
const csvField = (value: string): string => {
  if (value === '') return '';
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

/**
 * Joins ordered CSV cells into a single row.
 *
 * @param {string[]} cells - Already-escaped cells.
 * @returns {string} Comma-joined row.
 */
const csvRow = (cells: string[]): string => cells.join(',');

// --- Hook ----------------------------------------------------------------

/**
 * Parses the BulkUpsert response (CSV-as-JSON) and pulls out the data row's
 * Id and Error columns. The response shape is:
 *   [
 *     [header columns...],
 *     ['Format Version','5',...],
 *     [data row values...],
 *   ]
 *
 * @param {unknown} responseJson - Raw response from odata.post.
 * @returns {BulkUpsertCampaignBrandKitResponse} Parsed result.
 */
const parseResponse = (responseJson: unknown): BulkUpsertCampaignBrandKitResponse => {
  if (!Array.isArray(responseJson) || responseJson.length < 3) {
    return {};
  }
  const [header, , dataRow] = responseJson as string[][];
  if (!Array.isArray(header) || !Array.isArray(dataRow)) {
    return {};
  }

  const raw: Record<string, string> = {};
  header.forEach((col, i) => {
    raw[col] = dataRow[i];
  });

  const idIndex = header.indexOf('Id');
  const errorIndex = header.indexOf('Error');

  return {
    campaignId: idIndex >= 0 ? dataRow[idIndex] : undefined,
    error: errorIndex >= 0 ? dataRow[errorIndex] : undefined,
    raw,
  };
};

/**
 * Hook that posts a Default.BulkUpsert request to create or update a PMax
 * campaign and its campaign-level brand kit. See `BulkUpsertCampaignBrandKitRequest`
 * for the supported fields.
 *
 * @returns {Function} bulkUpsert - accepts a request, returns parsed response.
 */
export const useBulkUpsertCampaignBrandKit = () => {
  const [odataOptions, { cid, aid }] = useOdataOptions();

  type Req = BulkUpsertCampaignBrandKitRequest;
  type Res = BulkUpsertCampaignBrandKitResponse;
  return useCallback(async (request: Req): Promise<Res> => {
    const {
      campaignId,
      campaignName,
      status = 'Paused',
      timeZone,
      budget,
      budgetType = 'DailyBudgetStandard',
      language = 'All',
      businessName,
      brandColors,
      brandLogos,
      termExclusions,
      messagingRestrictions,
    } = request;

    // Build columns in a stable order. Always include Id when updating so MT
    // can target the existing campaign.
    const isUpdate = campaignId !== undefined && campaignId !== '';
    const headerCells: string[] = ['Type', 'Name'];
    const dataCells: string[] = ['Campaign', ''];
    const fmtCells: string[] = ['Format Version', '5'];

    if (isUpdate) {
      headerCells.push('Id');
      dataCells.push(String(campaignId));
      fmtCells.push('');
    }

    headerCells.push('Status', 'Campaign', 'Time Zone', 'Budget', 'Budget Type', 'Campaign Type', 'Language');
    dataCells.push(
      status,
      csvField(campaignName),
      timeZone,
      String(budget),
      budgetType,
      'PerformanceMax',
      language
    );
    fmtCells.push('', '', '', '', '', '', '');

    // Append brand-kit columns when their inputs are present.
    if (businessName !== undefined) {
      headerCells.push('Business Name');
      dataCells.push(csvField(businessName));
      fmtCells.push('');
    }
    if (brandColors !== undefined) {
      headerCells.push('Brand Color');
      dataCells.push(brandColors.length > 0
        ? csvQuoteJson(textAssetArrayJson(brandColors))
        : 'delete_value');
      fmtCells.push('');
    }
    if (brandLogos !== undefined) {
      headerCells.push('Brand Logo');
      dataCells.push(brandLogos.length > 0
        ? csvQuoteJson(brandLogoArrayJson(brandLogos))
        : 'delete_value');
      fmtCells.push('');
    }
    if (termExclusions !== undefined) {
      headerCells.push('Term Exclusions');
      dataCells.push(termExclusions.length > 0
        ? csvQuoteJson(textAssetArrayJson(termExclusions))
        : 'delete_value');
      fmtCells.push('');
    }
    if (messagingRestrictions !== undefined) {
      headerCells.push('Messaging Restrictions');
      dataCells.push(messagingRestrictions.length > 0
        ? csvQuoteJson(textAssetArrayJson(messagingRestrictions))
        : 'delete_value');
      fmtCells.push('');
    }

    const csvRows = [
      csvRow(headerCells),
      csvRow(fmtCells),
      csvRow(dataCells),
    ];

    const url = `Customers(${cid})/Accounts(${aid})/Default.BulkUpsert`;
    const responseJson = await odata.post({
      ...odataOptions,
      url,
      data: { CsvRows: csvRows },
    });

    return parseResponse(responseJson);
  }, [odataOptions, cid, aid]);
};
